import { uploadData, downloadData, list, remove, getUrl } from 'aws-amplify/storage';

export const getBlogPosts = async () => {
  try {
    console.log('Fetching blog posts from Amplify Gen 2 Storage...');
    
    const result = await list({
      path: 'public/blog-posts/',
      options: {
        listAll: true
      }
    });
    
    console.log('Listed files:', result);
    const posts = [];
    
    for (const item of result.items) {
      if (item.path.endsWith('.json')) {
        try {
          const downloadResult = await downloadData({
            path: item.path
          }).result;
          
          const text = await downloadResult.body.text();
          const post = JSON.parse(text);
          const id = item.path.replace('public/blog-posts/', '').replace('.json', '');
          
          console.log('Full post data loaded:', post);
          console.log('Post data loaded:', { id, title: post.title, published: post.published, category: post.category, date: post.date });
          
          // Clean up old category data
          if (post.category === 'DATA-ARCHITECTURE' || post.category === 'data-architecture') {
            post.category = 'Data Preparation';
          }
          
          if (post.published === true) {
            posts.push({ ...post, id });
          }
        } catch (error) {
          console.error(`Error loading post ${item.path}:`, error);
        }
      }
    }
    
    return posts.sort((a, b) => new Date(b.date || b.created || 0) - new Date(a.date || a.created || 0));
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    return [];
  }
};

export const getAllBlogPosts = async () => {
  try {
    console.log('Fetching ALL blog posts (including drafts) from Amplify Gen 2 Storage...');
    
    const result = await list({
      path: 'public/blog-posts/',
      options: {
        listAll: true
      }
    });
    
    const posts = [];
    
    for (const item of result.items) {
      if (item.path.endsWith('.json')) {
        try {
          const downloadResult = await downloadData({
            path: item.path
          }).result;
          
          const text = await downloadResult.body.text();
          const post = JSON.parse(text);
          const id = item.path.replace('public/blog-posts/', '').replace('.json', '');
          
          // Clean up old category data
          if (post.category === 'DATA-ARCHITECTURE' || post.category === 'data-architecture') {
            post.category = 'Data Preparation';
          }
          
          // Include ALL posts (published and unpublished)
          posts.push({ ...post, id });
        } catch (error) {
          console.error(`Error loading post ${item.path}:`, error);
        }
      }
    }
    
    return posts.sort((a, b) => new Date(b.date || b.created || 0) - new Date(a.date || a.created || 0));
  } catch (error) {
    console.error('Error fetching all blog posts:', error);
    return [];
  }
};

export const getBlogPost = async (id) => {
  try {
    const downloadResult = await downloadData({
      path: `public/blog-posts/${id}.json`
    }).result;
    
    const text = await downloadResult.body.text();
    return JSON.parse(text);
  } catch (error) {
    console.error('Error fetching blog post:', error);
    return null;
  }
};

export const getBlogImageUrl = async (fileName) => {
  try {
    const result = await getUrl({
      path: `public/blog-images/${fileName}`
    });
    return result.url;
  } catch (error) {
    console.error('Error getting blog image URL:', error);
    return null;
  }
};

export const processBlogContent = async (content) => {
  let processedContent = content;
  
  // Process images first
  const imageRegex = /\[IMAGE:\s*([^\]]+)\]/g;
  const matches = content.match(imageRegex);
  
  if (matches) {
    for (const match of matches) {
      const fileName = match.replace(/\[IMAGE:\s*([^\]]+)\]/, '$1').trim();
      const imageUrl = await getBlogImageUrl(fileName);
      if (imageUrl) {
        processedContent = processedContent.replace(match, `<img src="${imageUrl}" alt="${fileName}" class="blog-image" />`);
      }
    }
  }
  
  // Convert markdown-style formatting to HTML
  processedContent = processedContent
    // Headers
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    // Bold text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Italic text
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Bullet points
    .replace(/^• (.+)$/gm, '<li>$1</li>')
    // Section dividers
    .replace(/⸻/g, '<hr class="section-divider">')
    // Double line breaks to paragraphs
    .replace(/\n\n/g, '</p><p>')
    // Wrap in paragraph tags
    .replace(/^(.)/gm, '<p>$1')
    .replace(/(.+)$/gm, '$1</p>')
    // Fix list items
    .replace(/<p><li>/g, '<li>')
    .replace(/<\/li><\/p>/g, '</li>')
    // Wrap consecutive list items in ul tags
    .replace(/(<li>.*?<\/li>)/gs, (match) => {
      const items = match.match(/<li>.*?<\/li>/g);
      return items ? `<ul>${items.join('')}</ul>` : match;
    })
    // Clean up empty paragraphs
    .replace(/<p><\/p>/g, '')
    .replace(/<p>\s*<\/p>/g, '');
  
  return processedContent;
};

export const saveBlogPost = async (post) => {
  try {
    const id = post.id || `post-${Date.now()}`;
    const postData = {
      ...post,
      id,
      lastModified: new Date().toISOString()
    };
    
    console.log('Blog service saving:', postData);
    
    await uploadData({
      path: `public/blog-posts/${id}.json`,
      data: JSON.stringify(postData, null, 2),
      options: {
        contentType: 'application/json'
      }
    }).result;
    
    console.log('Post saved successfully with ID:', id);
    return id;
  } catch (error) {
    console.error('Error saving blog post:', error);
    throw error;
  }
};

export const deleteBlogPost = async (id) => {
  try {
    await remove({
      path: `public/blog-posts/${id}.json`
    });
  } catch (error) {
    console.error('Error deleting blog post:', error);
    throw error;
  }
};

export const getBlogImages = async () => {
  try {
    const result = await list({
      path: 'public/blog-images/',
      options: {
        listAll: true
      }
    });
    return result.items.filter(item => item.path.match(/\.(jpg|jpeg|png|gif|webp)$/i));
  } catch (error) {
    console.error('Error fetching blog images:', error);
    return [];
  }
};

export const uploadBlogImage = async (file, fileName) => {
  try {
    const result = await uploadData({
      path: `public/blog-images/${fileName}`,
      data: file,
      options: {
        contentType: file.type
      }
    }).result;
    
    return result.path;
  } catch (error) {
    console.error('Error uploading blog image:', error);
    throw error;
  }
};